import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteGeneratedReport,
  downloadGeneratedReport,
  errorMessage,
  fetchGeneratedReports,
  refreshGeneratedReport,
  type GeneratedReportSummary,
} from './api';
import { saveDownloadedFile } from './downloadFile';

const dateTime = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function lensLabel(lens: string) {
  return lens === 'apertura' ? 'Apertura in zona' : 'Analisi attività';
}

function DossierIcon({ kind }: { kind: 'file' | 'download' | 'refresh' | 'trash' }) {
  if (kind === 'download') return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"/></svg>;
  if (kind === 'refresh') return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M19 8a7.5 7.5 0 1 0 .3 7.3M19 4v4h-4"/></svg>;
  if (kind === 'trash') return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 7h14M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5"/></svg>;
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 3h7l4 4v14H7zM14 3v5h4M10 12h5m-5 4h5"/></svg>;
}

export default function ReportDossiers() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const reports = useQuery({ queryKey: ['generated-reports'], queryFn: fetchGeneratedReports });
  const ordered = [...(reports.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const selected = ordered.find((report) => report.id === selectedId) ?? null;
  const download = useMutation({ mutationFn: downloadGeneratedReport, onSuccess: saveDownloadedFile });
  const refresh = useMutation({
    mutationFn: refreshGeneratedReport,
    onSuccess: (file) => {
      saveDownloadedFile(file);
      setNotice('Creato un nuovo dossier con i dati di oggi; il precedente resta nello storico');
      queryClient.invalidateQueries({ queryKey: ['generated-reports'] });
    },
  });
  const remove = useMutation({
    mutationFn: deleteGeneratedReport,
    onSuccess: (_, id) => {
      queryClient.setQueryData<GeneratedReportSummary[]>(['generated-reports'], (current) => current?.filter((report) => report.id !== id) ?? []);
      setConfirmDelete(false);
      setSelectedId(null);
      setNotice('Dossier eliminato dallo storico.');
    },
  });

  const open = (id: string) => {
    setSelectedId(id);
    setConfirmDelete(false);
    setNotice(null);
    download.reset();
    refresh.reset();
    remove.reset();
  };

  if (selected) return <DossierDetail
    report={selected}
    confirmDelete={confirmDelete}
    setConfirmDelete={setConfirmDelete}
    notice={notice}
    onBack={() => { setSelectedId(null); setConfirmDelete(false); setNotice(null); }}
    onDownload={() => download.mutate(selected.id)}
    onRefresh={() => refresh.mutate(selected.id)}
    onDelete={() => remove.mutate(selected.id)}
    downloadPending={download.isPending}
    refreshPending={refresh.isPending}
    deletePending={remove.isPending}
    error={download.error ?? refresh.error ?? remove.error}
  />;

  return <section className="report-archive" aria-labelledby="report-archive-title">
    <header><div><p className="eyebrow">Archivio personale</p><h2 id="report-archive-title">I tuoi dossier</h2><p>Ogni aggiornamento crea una nuova versione: quelle precedenti restano consultabili.</p></div>{ordered.length > 0 && <span>{ordered.length}</span>}</header>
    {notice && <p className="report-archive-toast" role="status">{notice}</p>}
    {reports.isPending && <div className="report-archive-loading" role="status"><i/><i/><i/><span>Recupero i dossier…</span></div>}
    {reports.isError && <div className="notice notice-error"><p>{errorMessage(reports.error)}</p><button type="button" className="btn-secondary" onClick={() => reports.refetch()}>Riprova</button></div>}
    {reports.isSuccess && ordered.length === 0 && <div className="report-archive-empty"><DossierIcon kind="file"/><h3>Nessun dossier creato</h3><p>I dossier si creano dal dettaglio di un’attività.</p><Link className="btn-secondary" to="/competitor">Apri un’analisi attività</Link></div>}
    {ordered.length > 0 && <ol className="report-archive-list">{ordered.map((report) => <li key={report.id}><button type="button" onClick={() => open(report.id)} aria-label={`Apri dossier ${report.title} del ${dateTime.format(new Date(report.createdAt))}`}><span className="report-archive-file"><DossierIcon kind="file"/></span><span className="report-archive-main"><strong>{report.title}</strong><span className={`report-lens is-${report.lens === 'apertura' ? 'opening' : 'analysis'}`}>{lensLabel(report.lens)}</span><time dateTime={report.createdAt}>{dateTime.format(new Date(report.createdAt))}</time></span><span className="report-archive-arrow" aria-hidden="true">→</span></button></li>)}</ol>}
  </section>;
}

interface DossierDetailProps {
  report: GeneratedReportSummary;
  confirmDelete: boolean;
  setConfirmDelete: (value: boolean) => void;
  notice: string | null;
  onBack: () => void;
  onDownload: () => void;
  onRefresh: () => void;
  onDelete: () => void;
  downloadPending: boolean;
  refreshPending: boolean;
  deletePending: boolean;
  error: Error | null;
}

function DossierDetail({ report, confirmDelete, setConfirmDelete, notice, onBack, onDownload, onRefresh, onDelete, downloadPending, refreshPending, deletePending, error }: DossierDetailProps) {
  return <section className="report-dossier-detail" aria-labelledby="report-dossier-title">
    <button type="button" className="report-dossier-back" onClick={onBack}><span aria-hidden="true">←</span> Torna ai dossier</button>
    <header><span className="report-dossier-file"><DossierIcon kind="file"/></span><div><p className="eyebrow">Dossier archiviato</p><h2 id="report-dossier-title">{report.title}</h2></div></header>
    <dl><div><dt>Lente</dt><dd><span className={`report-lens is-${report.lens === 'apertura' ? 'opening' : 'analysis'}`}>{lensLabel(report.lens)}</span></dd></div><div><dt>Creato</dt><dd><time dateTime={report.createdAt}>{dateTime.format(new Date(report.createdAt))}</time></dd></div>{report.vat && <div><dt>Partita IVA</dt><dd>{report.vat}</dd></div>}</dl>
    {notice && <p className="report-archive-toast" role="status">{notice}</p>}
    <div className="report-dossier-actions">
      <button type="button" className="btn-primary" disabled={downloadPending || refreshPending || deletePending} onClick={onDownload}><DossierIcon kind="download"/>{downloadPending ? 'Preparo il PDF…' : 'Scarica PDF'}</button>
      <button type="button" className="btn-secondary" disabled={downloadPending || refreshPending || deletePending} onClick={onRefresh}><DossierIcon kind="refresh"/>{refreshPending ? 'Rigenero con i dati di oggi…' : 'Aggiorna i dati'}</button>
      {!confirmDelete ? <button type="button" className="report-dossier-delete" disabled={downloadPending || refreshPending || deletePending} onClick={() => setConfirmDelete(true)}><DossierIcon kind="trash"/>Elimina</button> : <div className="report-delete-confirm" role="alert"><strong>Eliminare questo dossier?</strong><p>Questa versione non sarà più disponibile. Le altre versioni restano nello storico.</p><div><button type="button" className="btn-quiet" disabled={deletePending} onClick={() => setConfirmDelete(false)}>Annulla</button><button type="button" className="btn-danger" disabled={deletePending} onClick={onDelete}>{deletePending ? 'Elimino…' : 'Elimina definitivamente'}</button></div></div>}
    </div>
    {error && <p className="report-dossier-error" role="alert">{errorMessage(error)}</p>}
  </section>;
}
